import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  Col,
  Container,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";
import { Bar, Line } from "react-chartjs-2";
import { timerTrackerService } from "../../services/timeTrackerServices";
import DateRangePicker from "../common/DateRangePicker";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const DEFAULT_DATE_RANGES = {
  cashFlow: {
    startDate: moment()
      .subtract(6, "months")
      .startOf("day")
      .toDate(),
    endDate: moment()
      .add(3, "months")
      .endOf("day")
      .toDate(),
    rangeType: "Month",
    rangeType: "Month",
    isPresetRange: true,
    key: "selection",
  },
  profitLoss: {
    startDate: moment()
      .subtract(2, "months")
      .startOf("day")
      .toDate(),
    endDate: moment()
      .endOf("day")
      .toDate(),
    rangeType: "Month",
    presetLabel: "Rolling 2 Months",
    isPresetRange: true,
    key: "selection",
  },
  sales: {
    startDate: moment()
      .subtract(12, "months")
      .startOf("day")
      .toDate(),
    endDate: moment()
      .endOf("day")
      .toDate(),
    rangeType: "Month",
    isPresetRange: true,
    key: "selection",
    presetLabel: "Rolling 12 Months",
  },
};

const toTexasISO = (date) => {
  // Create a new Date object with the same time as the input
  const texasDate = new Date(date.getTime());

  // Determine if the date falls within Daylight Saving Time for Texas
  const month = date.getMonth();
  const day = date.getDate();
  const dayOfWeek = date.getDay(); // 0 for Sunday, 1 for Monday, etc.

  // Check if the date is between the second Sunday in March and the first Sunday in November
  const isDST =
    (month > 2 && month < 10) || // April to October are always in DST
    (month === 2 && day - dayOfWeek >= 7) || // Second Sunday in March or later
    (month === 10 && day - dayOfWeek < 7); // Before the first Sunday in November

  const texasOffset = isDST ? 300 : 360; // 300 mins for CDT (UTC-5), 360 mins for CST (UTC-6)

  // Calculate the offset difference and adjust the time to Texas timezone
  const offsetDifference = date.getTimezoneOffset() - texasOffset;
  texasDate.setMinutes(texasDate.getMinutes() - offsetDifference);

  return texasDate;
};

const FinancialDashboard = ({ includeDisabledLogs }) => {
  const [dateRanges, setDateRanges] = useState({ ...DEFAULT_DATE_RANGES });
  const [cashFlowLoading, setCashFlowLoading] = useState(true);
  const [profitLossLoading, setProfitLossLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(true);

  const [salesChartData, setSalesChartData] = useState({
    dates: [],
    values: [],
    total: 0,
  });

  const [cashFlowChartData, setCashFlowChartData] = useState({
    dates: [],
    values: [],
    total: 0,
  });

  const [profitLossData, setProfitLossData] = useState({
    totalExpenses: 0,
    totalIncome: 0,
    profit: 0,
    verdict: "",
    totalJobs: 0,
  });

  const fetchSalesData = async () => {
    setSalesLoading(true);
    const { response } = await timerTrackerService.salesFinanceDashboardService(
      {
        filter: {
          startDateTime: dateRanges?.sales?.startDate
            ? toTexasISO(dateRanges?.sales?.startDate)
            : null,
          endDateTime: dateRanges?.sales?.endDate
            ? toTexasISO(dateRanges?.sales?.endDate)
            : null,
          status: includeDisabledLogs
            ? ["Approved", "Unapproved"]
            : ["Approved"],
        },
      }
    );

    if (response) {
      const dates = response.chartData.map((item) =>
        moment.utc(item.date).format("MMM DD")
      );
      const values = response.chartData.map((item) => parseFloat(item.value));

      setSalesChartData({
        dates,
        values,
        total: response.totalIncome,
      });
    }
    setSalesLoading(false);
  };

  const fetchProfitAndLossData = async () => {
    setProfitLossLoading(true);
    const {
      response,
    } = await timerTrackerService.profitAndLossFinanceDashboardService({
      filter: {
        startDateTime: dateRanges?.profitLoss?.startDate
          ? toTexasISO(dateRanges?.profitLoss?.startDate)
          : null,
        endDateTime: dateRanges?.profitLoss?.endDate
          ? toTexasISO(dateRanges?.profitLoss?.endDate)
          : null,
        status: includeDisabledLogs ? ["Approved", "Unapproved"] : ["Approved"],
      },
    });
    if (response) {
      setProfitLossData({
        totalExpenses: response.totalExpenses,
        totalIncome: response.totalIncome,
        profit: response.profit,
        verdict: response.verdict,
        totalJobs: response.totalJobs,
      });
    }
    setProfitLossLoading(false);
  };

  const fetchCashflowData = async () => {
    setCashFlowLoading(true);
    const {
      response,
    } = await timerTrackerService.cashFlowFinanceDashboardService({
      filter: {
        startDateTime: dateRanges?.cashFlow?.startDate
          ? toTexasISO(dateRanges?.cashFlow?.startDate)
          : null,
        endDateTime: dateRanges?.cashFlow?.endDate
          ? toTexasISO(dateRanges?.cashFlow?.endDate)
          : null,
        status: includeDisabledLogs ? ["Approved", "Unapproved"] : ["Approved"],
      },
    });

    if (response) {
      const dates = response.chartData.map((item) =>
        moment.utc(item.date).format("MMM DD")
      );
      const moneyIn = response.chartData.map((item) => parseFloat(item.sales));
      const moneyOut = response.chartData.map((item) =>
        parseFloat(item.expense)
      );

      setCashFlowChartData({
        dates,
        moneyIn,
        moneyOut,
        totalSales: response.totalSales,
        totalExpenses: response.totalExpenses,
      });
    }
    setCashFlowLoading(false);
  };

  const handleDateRangeChange = (chart, value) => {
    setDateRanges((prev) => ({
      ...prev,
      [chart]: value,
    }));
  };

  useEffect(() => {
    fetchSalesData();
  }, [dateRanges?.sales, includeDisabledLogs]);

  useEffect(() => {
    fetchProfitAndLossData();
  }, [dateRanges?.profitLoss, includeDisabledLogs]);

  useEffect(() => {
    fetchCashflowData();
  }, [dateRanges?.cashFlow, includeDisabledLogs]);

  const cashFlowData = useMemo(
    () => ({
      labels: cashFlowChartData.dates,
      datasets: [
        {
          label: "Money In",
          data: cashFlowChartData.moneyIn,
          backgroundColor: "#4CAF50",
          barPercentage: 0.4,
        },
        {
          label: "Money Out",
          data: cashFlowChartData.moneyOut,
          backgroundColor: "#00BCD4",
          barPercentage: 0.4,
        },
      ],
    }),
    [cashFlowChartData]
  );

  const salesDataConfig = useMemo(
    () => ({
      labels: salesChartData.dates,
      datasets: [
        {
          label: "Sales",
          data: salesChartData.values,
          borderColor: "#4CAF50",
          backgroundColor: "transparent",
          tension: 0.4,
          pointBackgroundColor: "#4CAF50",
          pointRadius: 3,
        },
      ],
    }),
    [salesChartData]
  );

  const cashFlowOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: false,
          grid: { display: false },
          ticks: { font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          ticks: {
            min: 0,
            max: Math.max(...cashFlowChartData?.values) * 1.2,
            callback: (value) => `$${value?.toFixed(2)}`,
            font: { size: 11 },
          },
          grid: { color: "#f0f0f0" },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || "";
              const value = context.parsed.y || 0;
              return `${label}: $${value?.toFixed(2)}`;
            },
          },
        },
      },
    }),
    []
  );

  const salesOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 } },
        },
        y: {
          min: 0,
          max: Math.max(...salesChartData.values) * 1.2,
          ticks: {
            callback: (value) => `$${value?.toFixed(2)}`,
            font: { size: 11 },
          },
          grid: { color: "#f0f0f0" },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          titleFont: { size: 12 },
          bodyFont: { size: 12 },
        },
      },
    }),
    [salesChartData.values]
  );

  const formatCurrency = (amount, abbreviate = false) => {
    if (abbreviate) {
      if (amount >= 1000) {
        return `$${(amount / 1000)?.toFixed(0)}K`;
      }
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const { incomeRatio, expensesRatio } = useMemo(() => {
    const total = profitLossData.totalIncome + profitLossData.totalExpenses;
    return {
      totalAmount: total,
      incomeRatio: total > 0 ? (profitLossData.totalIncome / total) * 100 : 0,
      expensesRatio:
        total > 0 ? (profitLossData.totalExpenses / total) * 100 : 0,
    };
  }, [profitLossData.totalIncome, profitLossData.totalExpenses]);

  return (
    <Container fluid className="p-3 p-md-3 position-relative">
      <Row>
        <Col>
          <Card className="border-1 rounded-lg">
            <Card.Body className="p-3 position-relative">
              {cashFlowLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    zIndex: 1,
                  }}
                >
                  <Spinner animation="border" role="status" />
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <h6 className="mb-0 text-uppercase fw-bold">CASH FLOW TREND</h6>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <DateRangePicker
                    value={dateRanges.cashFlow}
                    onChange={(ranges) => {
                      handleDateRangeChange("cashFlow", ranges?.selection);
                    }}
                    className="m-1"
                    allowNavigation
                    isFinancialDashboard
                  />
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ fontSize: "0.9rem" }}
                  >
                    <span className="me-1">Money in</span>
                    <span
                      className="badge rounded-circle"
                      style={{
                        backgroundColor: "#4CAF50",
                        height: "10px",
                        width: "10px",
                      }}
                    >
                      &nbsp;
                    </span>
                    <span className="me-1">Money out</span>
                    <span
                      className="badge rounded-circle"
                      style={{
                        backgroundColor: "#00BCD4",
                        height: "10px",
                        width: "10px",
                      }}
                    >
                      &nbsp;
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-end mb-2 d-flex justify-content-start gap-3 align-items-center">
                <div className="d-flex justify-content-center gap-2">
                  <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Sales
                  </span>
                  <h6>${cashFlowChartData?.totalSales?.toFixed(2)}</h6>{" "}
                </div>
                <h6>|</h6>
                <div className="d-flex justify-content-center gap-2">
                  <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Expenses{" "}
                  </span>
                  <h6>${cashFlowChartData?.totalExpenses?.toFixed(2)}</h6>{" "}
                </div>
              </div>
              <div style={{ height: "200px" }}>
                <Bar data={cashFlowData} options={cashFlowOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col lg={6}>
          <Card className="border-1 rounded-lg h-100">
            <Card.Body className="p-3 position-relative">
              {profitLossLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    zIndex: 1,
                  }}
                >
                  <Spinner animation="border" role="status" />
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 text-uppercase fw-bold">PROFIT & LOSS</h6>
                <DateRangePicker
                  value={dateRanges.profitLoss}
                  onChange={(ranges) =>
                    handleDateRangeChange("profitLoss", ranges?.selection)
                  }
                  className="m-1"
                  allowNavigation
                  isFinancialDashboard
                />
              </div>

              <div className="mt-3 d-flex flex-column justify-content-between">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex gap-2 align-items-center">
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Net {profitLossData.verdict}:
                    </div>
                    <h6
                      className={`mb-0 text-${
                        profitLossData.verdict === "Profit"
                          ? "success"
                          : "danger"
                      }`}
                    >
                      {formatCurrency(Math.abs(profitLossData.profit))}
                    </h6>
                  </div>
                  <Badge
                    bg={
                      profitLossData.verdict === "Profit" ? "success" : "danger"
                    }
                    className="px-3 py-2"
                  >
                    {profitLossData.verdict}
                  </Badge>
                </div>

                <div className="mt-5">
                  <div className="d-flex justify-content-between text-muted mb-2">
                    <span>Income</span>
                    <span>{formatCurrency(profitLossData.totalIncome)}</span>
                  </div>
                  <ProgressBar
                    variant="success"
                    now={incomeRatio}
                    style={{ height: "8px", borderRadius: "4px" }}
                    className="mb-3"
                  />
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between text-muted mb-2">
                    <span>Expenses</span>
                    <span>{formatCurrency(profitLossData.totalExpenses)}</span>
                  </div>
                  <ProgressBar
                    variant="danger"
                    now={expensesRatio}
                    style={{ height: "8px", borderRadius: "4px" }}
                  />
                </div>

                <div className="text-center mt-4">
                  <small className="text-muted">
                    {profitLossData.totalJobs} jobs processed
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mt-4 mt-lg-0">
          <Card className="border-1 rounded-lg h-100">
            <Card.Body className="p-3 position-relative">
              {salesLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    zIndex: 1,
                  }}
                >
                  <Spinner animation="border" role="status" />
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 text-uppercase fw-bold">SALES</h6>
                <DateRangePicker
                  value={dateRanges.sales}
                  onChange={(ranges) =>
                    handleDateRangeChange("sales", ranges?.selection)
                  }
                  className="m-1"
                  allowNavigation
                  isFinancialDashboard
                />
              </div>
              <div className="mb-2 d-flex gap-2 align-items-center">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Total sales:
                </div>
                <h6 className="mb-0">{formatCurrency(salesChartData.total)}</h6>
              </div>
              <div style={{ height: "230px" }}>
                <Line data={salesDataConfig} options={salesOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FinancialDashboard;
